import React, { useMemo, useState } from 'react';
import { useApp } from '../../context/SupabaseAppContext';
import { 
  MagnifyingGlassIcon,
  LightBulbIcon,
  ClipboardDocumentListIcon,
  UserIcon,
  TagIcon,
  CalendarIcon,
  StarIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { Idea, Task } from '../../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

interface SearchPageProps {
  onPageChange?: (page: string) => void;
  onEditIdea?: (idea: Idea) => void;
  onEditTask?: (task: Task) => void;
}

interface SearchResult {
  type: 'idea' | 'task';
  item: Idea | Task;
  matchedFields: string[];
  relevanceScore: number;
}

type ViewMode = 'list' | 'grid';
type FilterType = 'all' | 'ideas' | 'tasks';

const SearchPage: React.FC<SearchPageProps> = ({ onPageChange, onEditIdea, onEditTask }) => {
  const { state, actions } = useApp();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [localSearchQuery, setLocalSearchQuery] = useState(state.searchQuery);

  // Comprehensive search logic
  const searchResults = useMemo(() => {
    const query = localSearchQuery.toLowerCase().trim();
    if (!query) return [];

    const results: SearchResult[] = [];

    // Search Ideas
    state.ideas.forEach(idea => {
      const matchedFields: string[] = [];
      let relevanceScore = 0;

      // Unique ID match (very high weight)
      if (idea.uniqueId.toLowerCase().includes(query)) {
        matchedFields.push('uniqueId');
        relevanceScore += 20;
      }

      // Title match (highest weight)
      if (idea.title.toLowerCase().includes(query)) {
        matchedFields.push('title');
        relevanceScore += 10;
      }

      // Description match
      if (idea.description.toLowerCase().includes(query)) {
        matchedFields.push('description');
        relevanceScore += 5;
      }

      // Category match
      if (idea.category.toLowerCase().includes(query)) {
        matchedFields.push('category');
        relevanceScore += 3;
      }

      // Status match
      if (idea.status.toLowerCase().includes(query)) {
        matchedFields.push('status');
        relevanceScore += 3;
      }

      // Owner name match
      if (idea.owner.name.toLowerCase().includes(query)) {
        matchedFields.push('owner');
        relevanceScore += 2;
      }

      // Tags match
      const matchingTags = idea.tags.filter(tag => 
        tag.toLowerCase().includes(query)
      );
      if (matchingTags.length > 0) {
        matchedFields.push('tags');
        relevanceScore += matchingTags.length * 2;
      }

      // Comments match
      const hasCommentMatch = idea.comments?.some(comment =>
        comment.content.toLowerCase().includes(query)
      );
      if (hasCommentMatch) {
        matchedFields.push('comments');
        relevanceScore += 1;
      }

      if (matchedFields.length > 0) {
        results.push({
          type: 'idea',
          item: idea,
          matchedFields,
          relevanceScore
        });
      }
    });

    // Search Tasks
    state.tasks.forEach(task => {
      const matchedFields: string[] = [];
      let relevanceScore = 0;

      // Unique ID match (very high weight)
      if (task.uniqueId.toLowerCase().includes(query)) {
        matchedFields.push('uniqueId');
        relevanceScore += 20;
      }

      // Title match (highest weight)
      if (task.title.toLowerCase().includes(query)) {
        matchedFields.push('title');
        relevanceScore += 10;
      }

      // Description match
      if (task.description?.toLowerCase().includes(query)) {
        matchedFields.push('description');
        relevanceScore += 5;
      }

      // Status match
      if (task.status.toLowerCase().includes(query)) {
        matchedFields.push('status');
        relevanceScore += 3;
      }

      // Priority match
      if (task.priority.toLowerCase().includes(query)) {
        matchedFields.push('priority');
        relevanceScore += 3;
      }

      // Assignee name match
      if (task.assignee?.name.toLowerCase().includes(query)) {
        matchedFields.push('assignee');
        relevanceScore += 2;
      }

      // Comments match
      const hasCommentMatch = task.comments?.some(comment =>
        comment.content.toLowerCase().includes(query)
      );
      if (hasCommentMatch) {
        matchedFields.push('comments');
        relevanceScore += 1;
      }

      if (matchedFields.length > 0) {
        results.push({
          type: 'task',
          item: task,
          matchedFields,
          relevanceScore
        });
      }
    });

    // Sort by relevance score (highest first), then by date
    return results.sort((a, b) => {
      if (b.relevanceScore !== a.relevanceScore) {
        return b.relevanceScore - a.relevanceScore;
      }
      const aDate = a.type === 'idea' 
        ? new Date((a.item as Idea).dateSubmitted).getTime()
        : new Date((a.item as Task).dateCreated).getTime();
      const bDate = b.type === 'idea' 
        ? new Date((b.item as Idea).dateSubmitted).getTime()
        : new Date((b.item as Task).dateCreated).getTime();
      return bDate - aDate;
    });
  }, [localSearchQuery, state.ideas, state.tasks]);

  // Filter results by type
  const filteredResults = useMemo(() => {
    if (filterType === 'all') return searchResults;
    return searchResults.filter(result => {
      if (filterType === 'ideas') return result.type === 'idea';
      if (filterType === 'tasks') return result.type === 'task';
      return true;
    });
  }, [searchResults, filterType]);

  // Helper functions for display
  const getCategoryBadgeColor = (category: string) => {
    switch (category) {
      case 'Feature': return 'badge-primary';
      case 'Customer Request': return 'bg-cyan-100 text-cyan-800';
      case 'Product': return 'badge-success';
      case 'Process': return 'bg-orange-100 text-orange-800';
      case 'Technology': return 'bg-purple-100 text-purple-800';
      case 'Sales and Marketing': return 'bg-pink-100 text-pink-800';
      case 'Innovation': return 'badge-info';
      default: return 'badge-info';
    }
  };

  const getStatusBadgeColor = (status: string, type: 'idea' | 'task') => {
    if (type === 'idea') {
      switch (status) {
        case 'New': return 'bg-blue-100 text-blue-800';
        case 'Under Review': return 'bg-yellow-100 text-yellow-800';
        case 'In Progress': return 'bg-purple-100 text-purple-800';
        case 'Completed': return 'bg-emerald-100 text-emerald-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    } else {
      switch (status) {
        case 'To Do': return 'bg-blue-100 text-blue-800';
        case 'In Progress': return 'bg-purple-100 text-purple-800';
        case 'Done': return 'bg-emerald-100 text-emerald-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    }
  };

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-200 px-1 rounded">$1</mark>');
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    actions.setSearchQuery(localSearchQuery);
  };

  const handleResultClick = (result: SearchResult) => {
    if (result.type === 'idea') {
      onEditIdea?.(result.item as Idea);
    } else {
      onEditTask?.(result.item as Task);
    }
  };

  const resultCounts = {
    total: searchResults.length,
    ideas: searchResults.filter(r => r.type === 'idea').length,
    tasks: searchResults.filter(r => r.type === 'task').length
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FontAwesomeIcon icon={faSearch} className="mr-3 text-deep-teal" />
          Search Results
        </h1>
        <p className="text-gray-600 mt-2">
          Find ideas, tasks, and more across your workspace
        </p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <form onSubmit={handleSearchSubmit} className="flex gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search ideas, tasks, roadmap..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="input-field pl-10 pr-4"
            />
          </div>
          <button type="submit" className="btn-primary">
            Search
          </button>
        </form>
      </div>

      {/* Filters and View Controls */}
      <div className="mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as FilterType)}
              className="input-field min-w-0"
            >
              <option value="all">All Results ({resultCounts.total})</option>
              <option value="ideas">Ideas ({resultCounts.ideas})</option>
              <option value="tasks">Tasks ({resultCounts.tasks})</option>
            </select>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'list' ? 'bg-deep-teal text-white' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <ListBulletIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'grid' ? 'bg-deep-teal text-white' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <Squares2X2Icon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Results Summary */}
      {localSearchQuery && (
        <div className="mb-6 p-4 bg-ocean-mist rounded-lg">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">{filteredResults.length}</span> {filteredResults.length === 1 ? 'result' : 'results'} 
            {localSearchQuery && ` for "${localSearchQuery}"`}
          </p>
          {resultCounts.total > 0 && (
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-600">
              <span>{resultCounts.ideas} ideas</span>
              <span>{resultCounts.tasks} tasks</span>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {!localSearchQuery ? (
        <div className="text-center py-16">
          <FontAwesomeIcon icon={faSearch} className="h-16 w-16 text-gray-400 mx-auto mb-6" />
          <h3 className="text-xl font-medium text-gray-900 mb-4">Start your search</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Enter keywords in the search box above to find ideas, tasks, and more across your workspace.
          </p>
        </div>
      ) : filteredResults.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-gray-400 text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-medium text-gray-900 mb-4">No results found</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Try adjusting your search terms or browse ideas and tasks directly.
          </p>
          <div className="flex justify-center gap-4">
            <button onClick={() => onPageChange?.('ideas')} className="btn-outline">
              Browse Ideas
            </button>
            <button onClick={() => onPageChange?.('tasks')} className="btn-outline">
              Browse Tasks
            </button>
          </div>
        </div>
      ) : (
        <div className={
          viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
            : 'space-y-4'
        }>
          {filteredResults.map((result, index) => {
            const isIdea = result.type === 'idea';
            const idea = isIdea ? result.item as Idea : null;
            const task = !isIdea ? result.item as Task : null;

            return (
              <div
                key={`${result.type}-${result.item.id}-${index}`}
                className={`bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow cursor-pointer ${
                  viewMode === 'grid' ? 'p-4' : 'p-6'
                }`}
                onClick={() => handleResultClick(result)}
              >
                <div className={`flex ${viewMode === 'grid' ? 'flex-col' : 'items-start'} space-x-0 ${viewMode === 'list' ? 'lg:space-x-4' : ''}`}>
                  {/* Icon */}
                  <div className={`flex-shrink-0 p-3 rounded-lg mb-3 ${viewMode === 'list' ? 'lg:mb-0' : ''} ${
                    isIdea ? 'bg-electric-lime bg-opacity-20' : 'bg-energetic-blue bg-opacity-20'
                  }`}>
                    {isIdea ? (
                      <LightBulbIcon className="h-6 w-6 text-electric-lime" />
                    ) : (
                      <ClipboardDocumentListIcon className="h-6 w-6 text-energetic-blue" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        isIdea ? 'bg-electric-lime bg-opacity-20 text-electric-lime' : 'bg-energetic-blue bg-opacity-20 text-energetic-blue'
                      }`}>
                        {isIdea ? 'Idea' : 'Task'}
                      </span>
                      
                      {isIdea && (
                        <span className={`badge text-xs ${getCategoryBadgeColor(idea!.category)}`}>
                          {idea!.category}
                        </span>
                      )}
                      
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        getStatusBadgeColor(result.item.status, result.type)
                      }`}>
                        {result.item.status}
                      </span>

                      {task && (
                        <span className={`text-xs px-2 py-1 rounded-full ${getPriorityBadgeColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 mb-2">
                      <h4 
                        className="font-semibold text-gray-900"
                        dangerouslySetInnerHTML={{ 
                          __html: highlightText(result.item.title, localSearchQuery) 
                        }}
                      />
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full font-mono">
                        {result.item.uniqueId}
                      </span>
                    </div>
                    
                    {(idea?.description || task?.description) && (
                      <p 
                        className={`text-sm text-gray-600 mb-3 ${viewMode === 'grid' ? 'line-clamp-3' : 'line-clamp-2'}`}
                        dangerouslySetInnerHTML={{ 
                          __html: highlightText(
                            idea?.description || task?.description || '', 
                            localSearchQuery
                          ) 
                        }}
                      />
                    )}

                    {/* Meta information */}
                    <div className={`flex ${viewMode === 'grid' ? 'flex-col space-y-2' : 'items-center'} text-xs text-gray-500 ${viewMode === 'list' ? 'space-x-4' : ''}`}>
                      <div className="flex items-center">
                        <UserIcon className="h-3 w-3 mr-1" />
                        <span>
                          {isIdea ? idea!.owner.name : task?.assignee?.name || 'Unassigned'}
                        </span>
                      </div>
                      
                      <div className="flex items-center">
                        <CalendarIcon className="h-3 w-3 mr-1" />
                        <span>
                          {format(
                            new Date(isIdea ? idea!.dateSubmitted : task!.dateCreated), 
                            'MMM d, yyyy'
                          )}
                        </span>
                      </div>

                      {isIdea && idea!.score && (
                        <div className="flex items-center">
                          <StarIcon className="h-3 w-3 mr-1" />
                          <span>Score: {idea!.score.toFixed(1)}</span>
                        </div>
                      )}

                      {isIdea && idea!.tags.length > 0 && (
                        <div className="flex items-center">
                          <TagIcon className="h-3 w-3 mr-1" />
                          <span className="truncate">
                            {idea!.tags.slice(0, 2).join(', ')}
                            {idea!.tags.length > 2 && '...'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Matched fields indicator */}
                    <div className="mt-3 flex flex-wrap gap-1">
                      {result.matchedFields.map(field => (
                        <span
                          key={field}
                          className="text-xs bg-deep-teal bg-opacity-10 text-deep-teal px-2 py-1 rounded-full"
                        >
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SearchPage; 